import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  active: boolean;
}

interface TenantContextType {
  tenants: Tenant[];
  selectedTenantId: string;
  selectedTenant: Tenant | null;
  setSelectedTenantId: (id: string) => void;
  loading: boolean;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    let savedTenantId = localStorage.getItem('selected_tenant_id');

    // Tentar recuperar do token se não estiver no localStorage
    if (!savedTenantId && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.tenantId) {
          savedTenantId = payload.tenantId;
        }
      } catch (e) {
        // Silently fail if token is malformed
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (savedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = savedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }, []);

  const loadTenants = useCallback(async () => {
    // Carregar tenant selecionado do localStorage ou selecionar primeiro
    const savedTenantId = localStorage.getItem('selected_tenant_id');

    if (user?.role !== 'SUPERADMIN') {
      // Para usuários ADMIN/USER, usar o tenantId do próprio usuário
      if (user?.tenantId) {
        setSelectedTenantId(user.tenantId);
        if (savedTenantId !== user.tenantId) {
          localStorage.setItem('selected_tenant_id', user.tenantId);
        }
      } else {
        // Se não tem tenantId, limpar localStorage antigo
        localStorage.removeItem('selected_tenant_id');
      }
      setLoading(false);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/tenants');
      if (response.ok) {
        const data = await response.json();
        const tenantsArray = data.tenants || [];
        setTenants(tenantsArray);

        if (savedTenantId && tenantsArray.some((t: Tenant) => t.id === savedTenantId)) {
          setSelectedTenantId(savedTenantId);
        } else {
          // TenantId salvo é inválido, limpar e selecionar o primeiro
          if (savedTenantId) {
            console.warn('TenantId salvo no localStorage é inválido, limpando:', savedTenantId);
            localStorage.removeItem('selected_tenant_id');
          }

          if (tenantsArray.length > 0) {
            setSelectedTenantId(tenantsArray[0].id);
            localStorage.setItem('selected_tenant_id', tenantsArray[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [user, authenticatedFetch]);

  const refreshTenants = useCallback(async () => {
    setLoading(true);
    await loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Salvar tenant selecionado no localStorage
  useEffect(() => {
    if (selectedTenantId) {
      localStorage.setItem('selected_tenant_id', selectedTenantId);
    }
  }, [selectedTenantId]);

  const selectedTenant = tenants.find(t => t.id === selectedTenantId) || null;

  const value: TenantContextType = {
    tenants,
    selectedTenantId,
    selectedTenant,
    setSelectedTenantId,
    loading,
    refreshTenants,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/* eslint-disable-next-line react-refresh/only-export-components */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant deve ser usado dentro de um TenantProvider');
  }
  return context;
}
